import { alpha, styled } from '@mui/material';
import type { MouseEvent, ReactNode } from 'react';

type Props = {
  label: string;
  href: string;
  /** Outlined design system icon announcing the destination. */
  icon?: ReactNode;
  /** The current page belongs to this destination: primary tint and `aria-current="page"`. */
  selected?: boolean;
  /** Short status under the label, e.g. "Google Drive non connecté", marked with a warning dot. */
  alert?: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  'data-testid'?: string;
};

type OwnerState = {
  selected: boolean;
  hasIcon: boolean;
};

const ICON_SIZE = 20;
const ALERT_DOT_SIZE = 8;

const ItemLink = styled('a')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  ...(ownerState.selected ? theme.typography.body1Emphasis : theme.typography.body1),
  display: 'grid',
  gridTemplateColumns: ownerState.hasIcon ? `${ICON_SIZE}px minmax(0, 1fr)` : 'minmax(0, 1fr)',
  alignItems: 'center',
  columnGap: theme.spacing(1.5),
  minHeight: theme.spacing(5.5),
  paddingInline: theme.spacing(1.5),
  paddingBlock: theme.spacing(0.75),
  borderRadius: theme.shape.borderRadius,
  color: ownerState.selected ? theme.palette.primary.main : theme.palette.text.primary,
  backgroundColor: ownerState.selected
    ? alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity)
    : 'transparent',
  textDecoration: 'none',
  transition: theme.transitions.create('background-color', {
    duration: theme.transitions.duration.shortest,
  }),
  '@media (hover: hover)': {
    '&:hover': {
      backgroundColor: ownerState.selected
        ? alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity)
        : theme.palette.action.hover,
    },
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: -2,
  },
}));

const Icon = styled('span')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  display: 'inline-flex',
  flexShrink: 0,
  fontSize: ICON_SIZE,
  color: ownerState.selected ? theme.palette.primary.main : theme.palette.text.secondary,
  '& > svg': {
    fontSize: 'inherit',
  },
}));

const Label = styled('span')({
  // Last column, whether or not an icon leads.
  gridColumn: -2,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const Alert = styled('span')(({ theme }) => ({
  ...theme.typography.body2,
  gridColumn: -2,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  minWidth: 0,
  color: theme.palette.text.secondary,
  '&::before': {
    content: '""',
    flexShrink: 0,
    width: ALERT_DOT_SIZE,
    height: ALERT_DOT_SIZE,
    borderRadius: '50%',
    backgroundColor: theme.palette.warning.main,
  },
}));

/**
 * One destination of a `NavigationList`: a 44px link with an icon, a label and an optional status line.
 * The selected destination wears the primary tint and is announced as the current page.
 */
export function NavigationListItem(props: Props) {
  const ownerState = { selected: Boolean(props.selected), hasIcon: Boolean(props.icon) };

  return (
    <li data-testid={props['data-testid'] ?? 'navigation-list-item'}>
      <ItemLink
        href={props.href}
        onClick={props.onClick}
        aria-current={props.selected ? 'page' : undefined}
        ownerState={ownerState}
      >
        {props.icon ? <Icon ownerState={ownerState} aria-hidden="true">{props.icon}</Icon> : null}
        <Label>{props.label}</Label>
        {props.alert ? <Alert>{props.alert}</Alert> : null}
      </ItemLink>
    </li>
  );
}

export type { Props as NavigationListItemProps };
